/**
 * 매입관리 API 클라이언트 (Phase 7-1a) — backend /purchases/* 와 1:1.
 */

import { todayKst } from './date-label';
import type {
  AllMovementRow,
  ApiResponse,
  ApiResponseList,
  AuditEntry,
  CompanyId,
  ItemMovementRow,
  ItemStockBySpecRow,
  KeyItemCostRow,
  PurchaseCreateInput,
  PurchaseDetail,
  PurchaseHistoryRow,
  PurchaseListRow,
  PurchaseSupplierData,
  PurchaseType,
  PurchaseUpdateInput,
  PurchaseYearlyData,
} from './types';

const BASE = '/api';

export interface PurchaseListParams {
  page?: number;
  pageSize?: number;
  q?: string;
  companyId?: CompanyId;
  type?: PurchaseType;
  /** 입고일(actual_in_date) 기준 연도 다중 — 빈 배열이면 전체 */
  year?: number[];
  salesId?: string;
  /** 확인필요(국세청 자동등록 대기)만 */
  reviewOnly?: boolean;
}

export async function listPurchases(params: PurchaseListParams = {}): Promise<{
  data: PurchaseListRow[];
  page: { page: number; pageSize: number; total: number };
}> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params.q) qs.set('q', params.q);
  if (params.companyId) qs.set('companyId', params.companyId);
  if (params.type) qs.set('type', params.type);
  if (params.year && params.year.length > 0) {
    for (const y of params.year) qs.append('year', String(y));
  }
  if (params.salesId) qs.set('salesId', params.salesId);
  if (params.reviewOnly) qs.set('reviewOnly', 'true');

  const res = await fetch(`${BASE}/purchases?${qs.toString()}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponseList<PurchaseListRow>;
  if (!json.ok) throw new Error(json.error.message);
  return { data: json.data, page: json.page };
}

/** 영업 [+ 매입 연결] 모달용 — 품목별 가용 매입 집계 (재고보유 + 입고완료 + 미출고). */
export interface AvailablePurchaseByItem {
  itemCode: string;
  itemName: string | null;
  /**
   * 이 줄이 가리키는 **매입 규격**(purchase.description). 같은 품목코드라도 규격이 다르면
   * 다른 줄로 나온다 — '기타' 처럼 품목명을 뭉뚱그리고 규격만 직접 작성하는 운영 방식 때문.
   * null 인 경우 두 가지: ① 가용 재고 0 (검색 결과 — 연결 불가)
   * ② 규격이 빈 매입행 그룹 (availableQty > 0 — 규격 무관 FIFO 로 흘리면 다른 규격이
   *    빠져나가므로 화면에서 연결을 막고 규격 기재를 안내한다).
   */
  spec: string | null;
  /** 화면 부제용 — 규격이 있으면 규격, 없으면 품목 마스터 규격 */
  description: string | null;
  availableQty: number;
  oldestInDate: string | null;
  recentUnitPrice: number | null;
}
export async function listAvailableByItem(params: { q?: string; limit?: number } = {}): Promise<AvailablePurchaseByItem[]> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.limit) qs.set('limit', String(params.limit));
  const res = await fetch(`${BASE}/purchases/available-by-item?${qs.toString()}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<AvailablePurchaseByItem[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 입고일 기준 데이터 있는 연도 목록 (DESC) */
export async function listPurchaseYears(): Promise<string[]> {
  const res = await fetch(`${BASE}/purchases/years`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<string[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 매입처 distinct 목록 — 매입 등록 폼의 supplier 드롭다운용 (2026-05-14) */
export async function listSuppliers(): Promise<string[]> {
  const res = await fetch(`${BASE}/purchases/suppliers`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<string[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 단일 매입 detail (수정 모달용 — list 응답에서 뺀 컬럼까지 포함) */
export async function getPurchaseDetail(id: number): Promise<PurchaseDetail> {
  const res = await fetch(`${BASE}/purchases/${id}`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<PurchaseDetail>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 매입 변경 이력 (audit_log) — 수정 모달 하단 '변경 이력' 펼침용 */
export async function getPurchaseAudit(id: number): Promise<AuditEntry[]> {
  const res = await fetch(`${BASE}/purchases/${id}/audit`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<AuditEntry[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 같은 품목 최근 N건 — 행 클릭 모달용 */
export async function getPurchasesByItem(params: {
  itemCode?: string | null;
  itemName?: string | null;
  limit?: number;
}): Promise<PurchaseHistoryRow[]> {
  const qs = new URLSearchParams();
  if (params.itemCode) qs.set('itemCode', params.itemCode);
  if (params.itemName) qs.set('itemName', params.itemName);
  qs.set('limit', String(params.limit ?? 10));

  const res = await fetch(`${BASE}/purchases/by-item?${qs.toString()}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<PurchaseHistoryRow[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 같은 품목 입·출고 이벤트 — 재고 입·출고 내역 모달용 (입고/출고 각 1줄, 최근순) */
export async function getItemMovements(params: {
  itemCode?: string | null;
  itemName?: string | null;
  limit?: number;
}): Promise<ItemMovementRow[]> {
  const qs = new URLSearchParams();
  if (params.itemCode) qs.set('itemCode', params.itemCode);
  if (params.itemName) qs.set('itemName', params.itemName);
  qs.set('limit', String(params.limit ?? 30));

  const res = await fetch(`${BASE}/purchases/item-movements?${qs.toString()}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<ItemMovementRow[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 같은 품목의 현재고를 '직접 작성한 규격' 별로 나눈 것 — 재고 모달 규격별 수량 표시용 */
export async function getItemStockBySpec(params: {
  itemCode?: string | null;
  itemName?: string | null;
}): Promise<ItemStockBySpecRow[]> {
  const qs = new URLSearchParams();
  if (params.itemCode) qs.set('itemCode', params.itemCode);
  if (params.itemName) qs.set('itemName', params.itemName);

  const res = await fetch(`${BASE}/purchases/item-stock-by-spec?${qs.toString()}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<ItemStockBySpecRow[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 전체 품목 입·출고 내역 — 재고관리 '입출고내역' 탭용 (모든 품목, 시간순, 품목 칼럼 포함) */
export async function getAllMovements(limit = 20000): Promise<AllMovementRow[]> {
  const qs = new URLSearchParams({ limit: String(limit) });
  const res = await fetch(`${BASE}/purchases/all-movements?${qs.toString()}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<AllMovementRow[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 주요품목 원가 (탭 2) — v_stock + v_item_purchase_price */
export async function listKeyItemCosts(): Promise<KeyItemCostRow[]> {
  const res = await fetch(`${BASE}/purchases/key-item-costs`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<KeyItemCostRow[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 연도별 매입/출고 (탭 3) */
export async function getYearlyStats(year: number): Promise<PurchaseYearlyData> {
  const res = await fetch(`${BASE}/purchases/yearly-stats?year=${year}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<PurchaseYearlyData>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 주요 매입처 분기별 (탭 4, sales+ 만) */
export async function getSupplierQuarterly(
  year: number,
  topN: number = 5,
): Promise<PurchaseSupplierData> {
  const res = await fetch(
    `${BASE}/purchases/supplier-quarterly?year=${year}&topN=${topN}`,
    { credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<PurchaseSupplierData>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 매입관리 엑셀 (4시트) — 브라우저 다운로드 트리거 */
/**
 * 매입 목록 엑셀 다운로드 — 운영 서식 (매입관리대장) 템플릿 기반.
 * rows: 화면의 visibleRows (필터·정렬·검색 적용된 가시 행).
 */
export async function downloadPurchaseListExcel(rows: PurchaseListRow[]): Promise<void> {
  const res = await fetch(`${BASE}/purchases/export`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows }),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`엑셀 다운로드 실패 (${res.status}): ${txt}`);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const name = m ? decodeURIComponent(m[1]!) : `매입목록_${todayKst()}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadPurchaseExcel(year: number): Promise<void> {
  const res = await fetch(`${BASE}/analysis/purchase/export?year=${year}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const json = (await res.json()) as { error?: { message?: string } };
      if (json?.error?.message) msg = json.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const cd = res.headers.get('Content-Disposition') ?? '';
  const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const asciiMatch = cd.match(/filename="([^"]+)"/i);
  const name = utf8Match
    ? decodeURIComponent(utf8Match[1]!)
    : asciiMatch
      ? asciiMatch[1]!
      : `purchase_${year}.xlsx`;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface StockNegativeWarning {
  itemCode: string;
  itemName: string | null;
  currentStock: number;
}

export interface PurchaseSaveResult {
  purchaseId: number;
  warnings: StockNegativeWarning[];
}

export async function createPurchase(
  input: PurchaseCreateInput,
  force = false,
): Promise<PurchaseSaveResult> {
  const res = await fetch(`${BASE}/purchases`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(force ? { ...input, force: true } : input),
  });
  const json = (await res.json()) as ApiResponse<PurchaseSaveResult>;
  if (!json.ok) {
    if (!force && isConfirmable(json.error)) {
      if (window.confirm(`${json.error.message}`)) {
        return createPurchase(input, true);
      }
      throw new Error('취소되었습니다');
    }
    throw new Error(json.error.message);
  }
  return { purchaseId: json.data.purchaseId, warnings: json.data.warnings ?? [] };
}

/**
 * 확인 후 진행 가능한 경고 응답인지 판별 (백엔드 ApiError CONFLICT + details.reason).
 *   - STOCK_NEGATIVE  : 음수 재고
 *   - COMPANY_MISMATCH: 영업↔매입 사업자 교차 (정상 업무라 차단 않고 확인만)
 *   - IMPORT_LINKED   : 수출입 오더의 라인인 매입 삭제 (v2.75.0 — 오더에서도 사라진다는 확인)
 *   - LEGACY_COST_BACKFILLED : 과거 판보 원가가 소급 반영된 영업건에 매입을 새로 붙임 (v2.169.0 —
 *                     같은 원가가 두 번 잡힌다는 확인. 정당한 뒤늦은 매입도 있어 막지 않고 물어본다)
 * ⚠ 허용 목록(더하기)이다 — 새 reason 을 만들면 여기에도 넣어야 확인 창이 뜬다.
 */
function isConfirmable(error: { code: string; message: string; details?: unknown }): boolean {
  const reason = (error.details as { reason?: string } | undefined)?.reason;
  return error.code === 'CONFLICT'
    && (reason === 'STOCK_NEGATIVE' || reason === 'COMPANY_MISMATCH' || reason === 'IMPORT_LINKED'
      || reason === 'LEGACY_COST_BACKFILLED');
}

export async function updatePurchase(
  purchaseId: number,
  input: PurchaseUpdateInput,
  force = false,
): Promise<PurchaseSaveResult> {
  const res = await fetch(`${BASE}/purchases/${purchaseId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(force ? { ...input, force: true } : input),
  });
  const json = (await res.json()) as ApiResponse<PurchaseSaveResult>;
  if (!json.ok) {
    // 음수 재고 → 사용자 확인 후 강제 저장 재시도
    if (!force && isConfirmable(json.error)) {
      if (window.confirm(`${json.error.message}`)) {
        return updatePurchase(purchaseId, input, true);
      }
      throw new Error('취소되었습니다');
    }
    throw new Error(json.error.message);
  }
  return { purchaseId: json.data.purchaseId, warnings: json.data.warnings ?? [] };
}

export async function deletePurchase(
  purchaseId: number,
  force = false,
): Promise<PurchaseSaveResult> {
  const res = await fetch(
    `${BASE}/purchases/${purchaseId}${force ? '?force=true' : ''}`,
    { method: 'DELETE', credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<PurchaseSaveResult>;
  if (!json.ok) {
    if (!force && isConfirmable(json.error)) {
      if (window.confirm(`${json.error.message}`)) {
        return deletePurchase(purchaseId, true);
      }
      throw new Error('취소되었습니다');
    }
    throw new Error(json.error.message);
  }
  return { purchaseId: json.data.purchaseId, warnings: json.data.warnings ?? [] };
}

export interface BulkDeleteResult {
  deletedCount: number;
  warnings: StockNegativeWarning[];
}

/** 체크박스 다중 선택 일괄 삭제. 음수 재고 CONFLICT 시 확인 후 force 재시도 (단건과 동일 패턴). */
export async function bulkDeletePurchases(
  ids: number[],
  force = false,
): Promise<BulkDeleteResult> {
  const res = await fetch(`${BASE}/purchases/bulk-delete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, force }),
  });
  const json = (await res.json()) as ApiResponse<BulkDeleteResult>;
  if (!json.ok) {
    if (!force && isConfirmable(json.error)) {
      if (window.confirm(`${json.error.message}`)) {
        return bulkDeletePurchases(ids, true);
      }
      throw new Error('취소되었습니다');
    }
    throw new Error(json.error.message);
  }
  return { deletedCount: json.data.deletedCount, warnings: json.data.warnings ?? [] };
}

export interface PurchaseSplitInput {
  qty: number;
  salesId: string;
  actualOutDate?: string | null;
  salesCompanyId?: 'SM' | 'EJ' | null;
  actualSalesTo?: string | null;
  salesAmount?: number | null;
}

export interface PurchaseSplitResult {
  purchaseId: number;
  childPurchaseId: number | null;
  outQty: number;
  remainingQty: number;
  warnings: StockNegativeWarning[];
}

export async function splitPurchase(
  purchaseId: number,
  input: PurchaseSplitInput,
  force = false,
): Promise<PurchaseSplitResult> {
  const res = await fetch(`${BASE}/purchases/${purchaseId}/split`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(force ? { ...input, force: true } : input),
  });
  const json = (await res.json()) as ApiResponse<PurchaseSplitResult>;
  if (!json.ok) {
    // 분할도 영업건에 붙이는 경로 — 과거 판보 원가 소급분과 겹치면 확인 후 재시도 (v2.169.0).
    if (!force && isConfirmable(json.error)) {
      if (window.confirm(json.error.message)) {
        return splitPurchase(purchaseId, input, true);
      }
      throw new Error('취소되었습니다');
    }
    throw new Error(json.error.message);
  }
  return { ...json.data, warnings: json.data.warnings ?? [] };
}

/* ══════════════════════════════════════════════════════════════
 * 🚢 매입 ↔ 수출입 비용 이관 (2026-08-24 · v2.86.0)
 *
 * 수입 물류비가 판관비 매입으로 잘못 등록됐을 때 옮기는 통로.
 * ⚠수출입 탭에서 손으로 비용을 만들고 매입을 지우는 2단계로 하면 안 된다 —
 *   수동 등록에는 승인번호 칸이 없어 멱등키가 사라지고, 다음 달 업로드가
 *   같은 계산서를 또 만든다. 이 라우트가 승인번호를 함께 옮겨 준다.
 * ══════════════════════════════════════════════════════════════ */

export interface ToImportCostResult {
  importCostId: number;
  kind: '물류' | '부가세';
  totalKrw: number;
  detail: string;
  deletedPurchaseId: number;
}

/** 판관비 매입 → 수출입 비용(미배분). 이 매입 행은 삭제된다. */
export async function toImportCost(
  purchaseId: number,
  kind: '물류' | '부가세',
): Promise<ToImportCostResult> {
  const res = await fetch(`${BASE}/purchases/${purchaseId}/to-import-cost`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind }),
  });
  const json = (await res.json()) as ApiResponse<ToImportCostResult>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** ↩ 수출입 비용 → 매입 (이관 되돌리기). 원래 매입 행이 그대로 복원된다. */
export async function importCostBackToPurchase(
  importCostId: number,
): Promise<{ restoredPurchaseId: number; removedCostId: number }> {
  const res = await fetch(`${BASE}/purchases/import-cost/${importCostId}/back-to-purchase`, {
    method: 'POST',
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{ restoredPurchaseId: number; removedCostId: number }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}
